import { useEffect, useRef, useState } from "react";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { API_BASE_URL } from "../../constants";

const API_BASE = `${API_BASE_URL}/chat`;

export default function ChatBox() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [options, setOptions] = useState([]);
  const [expectInput, setExpectInput] = useState(false);

  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, options]);

  useEffect(() => {
    if (open && !sessionId) {
      startChat();
    }
  }, [open, sessionId]);

  async function startChat() {
    try {
      const res = await fetch(`${API_BASE}/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      const data = await res.json();

      setSessionId(data.sessionId);
      setOptions(data.options || []);
      setExpectInput(data.expectInput);
      setMessages([
        {
          sender: "BOT",
          text: data.botMessage,
        },
      ]);
    } catch (err) {
      console.error("Start chat failed", err);
    }
  }

  async function sendOption(optionId) {
    const selected = options.find((option) => option.id === optionId);

    setMessages((prev) => [
      ...prev,
      { sender: "USER", text: selected?.text ?? "" },
    ]);
    setOptions([]);

    const res = await fetch(`${API_BASE}/next`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sessionId,
        selectedOptionId: optionId,
      }),
    });

    const data = await res.json();

    setOptions(data.options || []);
    setExpectInput(data.expectInput);
    setMessages((prev) => [...prev, { sender: "BOT", text: data.botMessage }]);
  }

  async function handleSend(event) {
    event.preventDefault();
    if (!expectInput) {
      return;
    }

    const text = event.target.message.value.trim();
    if (!text) {
      return;
    }

    setMessages((prev) => [...prev, { sender: "USER", text }]);
    event.target.reset();

    const res = await fetch(`${API_BASE}/next`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sessionId,
        userInput: text,
      }),
    });

    const data = await res.json();

    setOptions(data.options || []);
    setExpectInput(data.expectInput);
    setMessages((prev) => [...prev, { sender: "BOT", text: data.botMessage }]);
  }

  return (
    <>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="fixed bottom-4 right-4 z-[9999] flex h-12 w-12 items-center justify-center rounded-full bg-primary-default 
        text-white shadow-xl transition-all duration-300 hover:scale-105 active:scale-95 sm:bottom-6 sm:right-6 sm:h-14 sm:w-14"
      >
        <i
          className={`fa-solid ${open ? "fa-xmark rotate-90" : "fa-message"
            } text-xl transition-transform duration-300`}
        />
      </button>

      <div
        className={`fixed bottom-20 right-4 z-[9998] flex h-[70vh] w-[calc(100vw-2rem)] max-w-80 origin-bottom-right flex-col overflow-hidden rounded-2xl bg-white shadow-2xl transition-all duration-500 ease-[cubic-bezier(.22,1,.36,1)] sm:bottom-24 sm:right-6 sm:h-[440px] ${open
            ? "translate-y-0 scale-100 opacity-100"
            : "pointer-events-none translate-y-6 scale-90 opacity-0"
          }`}
      >
        <div className="bg-primary-default px-5 py-4 text-center text-md font-semibold text-white">
          Welcome to WFS Chat
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto bg-white px-4 py-3 text-sm">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.sender === "USER" ? "justify-end" : "justify-start"
                }`}
            >
              <div
                className={`max-w-[75%] break-words rounded-xl px-3 py-2 leading-relaxed animate-fadeIn ${message.sender === "BOT"
                    ? "bg-gray-100 text-black"
                    : "bg-blue-100 text-blue-900"
                  }`}
              >
                {message.text}
              </div>
            </div>
          ))}

          <div ref={messagesEndRef} />
        </div>

        {options.length > 0 && !expectInput && (
          <div className="px-4 pb-3">
            <div className="max-h-40 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-inner animate-dropdown">
              {options.map((option, index) => (
                <button
                  key={option.id}
                  onClick={() => sendOption(option.id)}
                  className="group flex w-full items-center justify-between border-b bg-white px-4 py-3 text-left text-sm 
                  font-medium text-gray-800 transition-all duration-200 hover:bg-blue-50 hover:text-blue-700 active:bg-blue-100 last:border-b-0"
                  style={{ animationDelay: `${index * 40}ms` }}
                >
                  <span>{option.text}</span>

                  <i className="fa-solid fa-chevron-right translate-x-[-4px] text-xs text-gray-400 opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100" />
                </button>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSend} className="bg-white px-4 py-4">
          <div className="flex items-center gap-2 rounded-full border border-gray-200 px-3 py-2 shadow-sm focus-within:ring-1 focus-within:ring-blue-500">
            <input
              name="message"
              disabled={!expectInput}
              placeholder={
                expectInput
                  ? "Type your message..."
                  : "Please choose an option above"
              }
              className="flex-1 bg-transparent px-2 py-1 text-sm outline-none placeholder-gray-400 disabled:opacity-50"
            />

            <button
              type="submit"
              className="flex h-9 w-9 items-center justify-center rounded-3xl bg-primary-default text-white transition-all hover:bg-primary-default active:scale-95"
            >
              <i className="fa-solid fa-paper-plane mr-1 text-sm" />
            </button>
          </div>
        </form>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(6px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.25s ease-out;
        }

        @keyframes dropdown {
          from {
            opacity: 0;
            transform: translateY(-6px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .animate-dropdown {
          animation: dropdown 0.35s cubic-bezier(.22, 1, .36, 1);
        }
      `}</style>
    </>
  );
}
